import { getSupabaseClient } from "@veorest/api-client";
import { formatOrderDisplayNumber } from "@veorest/utils";
import type { TranslationKey } from "../i18n";
import type { PartnerDriverRow } from "./partnerDrivers";

export type { PartnerDriverRow } from "./partnerDrivers";
export { listPartnerDrivers } from "./partnerDrivers";

/** Locked Partner UI stages (canonical). */
export type PartnerOrderStage =
  | "pending"
  | "processing"
  | "ready"
  | "in_delivery"
  | "completed"
  | "cancelled";

export type PartnerOrderFilter = PartnerOrderStage | "all";

export const PARTNER_STAGE_LABELS: Record<PartnerOrderStage, string> = {
  pending: "Pending",
  processing: "Processing",
  ready: "Ready",
  in_delivery: "In delivery",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const PARTNER_FILTER_ORDER: PartnerOrderFilter[] = [
  "pending",
  "processing",
  "ready",
  "in_delivery",
  "completed",
  "cancelled",
  "all",
];

export interface PartnerOrderInfo {
  /** Normalised payment type from the logistics payload. */
  paymentType: "prepaid" | "cod" | "pay_at_cashier" | "unknown";
  paymentMethod: string | null;
  paymentStatus: string | null;
  pickupPaymentMode: string | null;
  /** CUSTOMER_PAYS | RESTAURANT_PAYS | OELOX_PAYS | FREE_DELIVERY | null */
  feeResponsibility: string | null;
  deliveryFee: number | null;
  /** 245: customer rejected the order — goods return to the store. */
  returnToStore: boolean;
  /** "driver" when the driver must collect cash. */
  cashCollector: string | null;
  /** per_order | percentage */
  partnerFeeType: string | null;
  partnerFeeAmount: number | null;
  prepMinutes: number | null;
  readyAt: string | null;
  deliveryLat: number | null;
  deliveryLng: number | null;
  /** Frozen distance (orders.delivery_km) — detail rows only. */
  deliveryKm: number | null;
  tenantLat: number | null;
  tenantLng: number | null;
}

export interface PartnerOrderRow {
  id: string;
  tenant_id: string;
  tenant_name: string;
  status: string;
  /** orders.channel — whatsapp | phone | storefront | pos … */
  channel: string | null;
  /** orders.source — staff | whatsapp_ai | storefront | marketplace_app … */
  source: string | null;
  fulfillment_type: "delivery" | "pickup";
  total: number;
  currency: string;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string | null;
  assigned_driver_id: string | null;
  driver_name: string | null;
  driver_status: string | null;
  handling_partner_id: string | null;
  partner_accepted_at: string | null;
  displayNumber: string;
  stage: PartnerOrderStage;
  created_at: string | null;
  updated_at: string | null;
  logistics?: Record<string, unknown> | null;
  /** Payment / fee / timing facts the partner needs on the desk. */
  info: PartnerOrderInfo;
}

export interface PartnerOrderItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  notes: string | null;
}

export interface PartnerOrderDetail extends PartnerOrderRow {
  items: PartnerOrderItem[];
  notes?: string | null;
}

export interface PartnerOrdersListResult {
  orders: PartnerOrderRow[];
  total: number;
}

export interface PartnerStageStats {
  pending: number;
  processing: number;
  ready: number;
  in_delivery: number;
  completed: number;
  /** Cancelled + rejected orders (rejection maps to the cancelled stage). */
  cancelled: number;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(v: unknown, fallback = ""): string {
  if (v == null) return fallback;
  return String(v);
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function strOrNull(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

/** Normalise the logistics payload + order columns into desk-ready facts. */
function mapPartnerOrderInfo(
  raw: Record<string, unknown>,
  logistics: Record<string, unknown> | null
): PartnerOrderInfo {
  const l = logistics ?? {};
  const paymentMethod = strOrNull(raw.payment_method ?? l.payment_method);
  const pickupPaymentMode = strOrNull(
    raw.pickup_payment_mode ?? l.pickup_payment_mode
  );
  const paymentStatus = strOrNull(raw.payment_status ?? l.payment_status);
  const feeResponsibility = strOrNull(
    raw.delivery_fee_responsibility ?? l.delivery_fee_responsibility
  );

  const declaredType = strOrNull(l.payment_type)?.toLowerCase();
  let paymentType: PartnerOrderInfo["paymentType"];
  if (
    declaredType === "prepaid" ||
    declaredType === "cod" ||
    declaredType === "pay_at_cashier"
  ) {
    paymentType = declaredType;
  } else {
    const method = (paymentMethod ?? "").toLowerCase();
    const status = (paymentStatus ?? "").toLowerCase();
    const pickup = (pickupPaymentMode ?? "").toUpperCase();
    if (status === "paid" || method === "card" || method === "wallet") {
      paymentType = "prepaid";
    } else if (method === "cod" || pickup === "COD") {
      paymentType = "cod";
    } else if (pickup === "PAY_AT_CASHIER" || method.startsWith("pay_at")) {
      paymentType = "pay_at_cashier";
    } else {
      paymentType = "unknown";
    }
  }

  return {
    paymentType,
    paymentMethod,
    paymentStatus,
    pickupPaymentMode,
    feeResponsibility,
    deliveryFee: numOrNull(raw.delivery_fee ?? l.delivery_fee),
    returnToStore: raw.return_to_store === true || l.return_to_store === true,
    cashCollector: strOrNull(l.cash_collector),
    partnerFeeType: strOrNull(raw.partner_fee_type ?? l.partner_fee_type),
    partnerFeeAmount: numOrNull(
      raw.partner_fee_amount ?? l.partner_fee_amount
    ),
    prepMinutes: numOrNull(raw.prep_minutes ?? l.prep_minutes),
    readyAt: strOrNull(raw.ready_at ?? l.ready_at),
    deliveryLat: numOrNull(raw.delivery_lat),
    deliveryLng: numOrNull(raw.delivery_lng),
    deliveryKm: numOrNull(raw.delivery_km),
    tenantLat: numOrNull(raw.tenant_lat),
    tenantLng: numOrNull(raw.tenant_lng),
  };
}

const DONE = new Set(["delivered", "completed"]);
const CANCELLED = new Set(["cancelled", "rejected", "canceled"]);
const PROCESSING = new Set([
  "approved",
  "in_kitchen",
  "accepted",
  "preparing",
  "pending_cashier_approval",
]);
const READY = new Set(["ready", "ready_for_dispatch"]);
const IN_DELIVERY = new Set([
  "blasted_to_drivers",
  "assigned",
  "picked_up",
  "out_for_delivery",
  "out",
]);

/** Map DB status → locked UI stage (canonical). */
export function statusToPartnerStage(status: string): PartnerOrderStage {
  const s = status.toLowerCase();
  if (CANCELLED.has(s)) return "cancelled";
  if (DONE.has(s)) return "completed";
  if (IN_DELIVERY.has(s)) return "in_delivery";
  if (READY.has(s)) return "ready";
  if (PROCESSING.has(s)) return "processing";
  if (s === "pending" || s === "draft") return "pending";
  return "pending";
}

/**
 * Partner logistics stage: accept gate first, then status mapping.
 * Matches partner Android dispatch rules while using locked labels.
 */
export function resolvePartnerStage(row: {
  status: string;
  partner_accepted_at: string | null;
  assigned_driver_id: string | null;
  fulfillment_type: string;
}): PartnerOrderStage {
  const status = row.status.toLowerCase();
  const statusStage = statusToPartnerStage(status);
  if (statusStage === "cancelled" || statusStage === "completed") {
    return statusStage;
  }

  const isDelivery = row.fulfillment_type.toLowerCase() === "delivery";
  if (isDelivery && !row.partner_accepted_at) {
    return "pending";
  }

  if (row.assigned_driver_id) {
    return "in_delivery";
  }

  if (IN_DELIVERY.has(status)) return "in_delivery";
  if (READY.has(status)) return "ready";
  if (PROCESSING.has(status)) return "processing";
  return statusStage;
}

export function mapPartnerOrder(raw: Record<string, unknown>): PartnerOrderRow {
  const fulfillment =
    str(raw.fulfillment_type || raw.order_type, "delivery").toLowerCase() ===
    "pickup"
      ? "pickup"
      : "delivery";
  const partnerAccepted =
    raw.partner_accepted_at != null ? str(raw.partner_accepted_at) : null;
  const assigned =
    raw.assigned_driver_id != null && str(raw.assigned_driver_id)
      ? str(raw.assigned_driver_id)
      : null;
  const status = str(raw.status);
  const stage = resolvePartnerStage({
    status,
    partner_accepted_at: partnerAccepted,
    assigned_driver_id: assigned,
    fulfillment_type: fulfillment,
  });

  return {
    id: str(raw.id),
    tenant_id: str(raw.tenant_id),
    tenant_name: str(raw.tenant_name, "Business"),
    status,
    channel: strOrNull(raw.channel),
    source: strOrNull(raw.source),
    fulfillment_type: fulfillment,
    total: num(raw.total),
    currency: str(raw.currency, "IQD"),
    customer_name: str(raw.customer_name, "Guest"),
    customer_phone: raw.customer_phone != null ? str(raw.customer_phone) : null,
    customer_address:
      raw.customer_address != null ? str(raw.customer_address) : null,
    assigned_driver_id: assigned,
    driver_name: raw.driver_name != null ? str(raw.driver_name) : null,
    driver_status: raw.driver_status != null ? str(raw.driver_status) : null,
    handling_partner_id:
      raw.handling_partner_id != null ? str(raw.handling_partner_id) : null,
    partner_accepted_at: partnerAccepted,
    displayNumber: formatOrderDisplayNumber(str(raw.id)),
    stage,
    created_at: raw.created_at != null ? str(raw.created_at) : null,
    updated_at: raw.updated_at != null ? str(raw.updated_at) : null,
    logistics: asRecord(raw.logistics),
    info: mapPartnerOrderInfo(raw, asRecord(raw.logistics)),
  };
}

export interface PartnerDriverOrderRow extends PartnerOrderRow {
  /** From the driver's settlement row when the trip settled. */
  driver_earnings: number | null;
  cash_collected: number | null;
}

/** 241: paginated orders history for ONE partner driver (strict scope). */
export async function fetchPartnerDriverOrders(
  partnerId: string,
  driverId: string,
  opts?: { limit?: number; offset?: number }
): Promise<{ orders: PartnerDriverOrderRow[]; total: number }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_driver_orders", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_limit: opts?.limit ?? 10,
    p_offset: opts?.offset ?? 0,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.orders) ? row.orders : [];
  return {
    total: num(row.total),
    orders: list
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map((r) => ({
        ...mapPartnerOrder(r),
        driver_earnings:
          r.driver_earnings == null ? null : num(r.driver_earnings),
        cash_collected: r.cash_collected == null ? null : num(r.cash_collected),
      })),
  };
}

export function computeStageStats(orders: PartnerOrderRow[]): PartnerStageStats {
  const stats: PartnerStageStats = {
    pending: 0,
    processing: 0,
    ready: 0,
    in_delivery: 0,
    completed: 0,
    cancelled: 0,
  };
  for (const o of orders) {
    if (o.stage === "pending") stats.pending += 1;
    else if (o.stage === "processing") stats.processing += 1;
    else if (o.stage === "ready") stats.ready += 1;
    else if (o.stage === "in_delivery") stats.in_delivery += 1;
    else if (o.stage === "completed") stats.completed += 1;
    else if (o.stage === "cancelled") stats.cancelled += 1;
  }
  return stats;
}

export function filterOrdersByStage(
  orders: PartnerOrderRow[],
  filter: PartnerOrderFilter
): PartnerOrderRow[] {
  if (filter === "all") return orders;
  return orders.filter((o) => o.stage === filter);
}

export function searchOrders(
  orders: PartnerOrderRow[],
  query: string
): PartnerOrderRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return orders;
  return orders.filter((o) => {
    return (
      o.customer_name.toLowerCase().includes(q) ||
      (o.customer_phone ?? "").toLowerCase().includes(q) ||
      o.tenant_name.toLowerCase().includes(q) ||
      o.displayNumber.toLowerCase().includes(q) ||
      o.id.toLowerCase().includes(q) ||
      (o.driver_name ?? "").toLowerCase().includes(q)
    );
  });
}

export async function listPartnerOrders(
  partnerId: string,
  opts?: { tenantId?: string; limit?: number; offset?: number }
): Promise<PartnerOrdersListResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_list_orders", {
    p_partner_id: partnerId,
    p_tenant_id: opts?.tenantId ?? null,
    p_status: null,
    p_limit: opts?.limit ?? 100,
    p_offset: opts?.offset ?? 0,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.orders) ? row.orders : [];
  return {
    orders: list
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map(mapPartnerOrder),
    total: num(row.total, list.length),
  };
}

export async function getPartnerOrder(
  partnerId: string,
  orderId: string
): Promise<PartnerOrderDetail> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_get_order", {
    p_partner_id: partnerId,
    p_order_id: orderId,
  });
  if (error) throw error;
  const row = asRecord(data);
  if (!row) throw new Error("order_not_found");
  const orderRaw = asRecord(row.order);
  if (!orderRaw) throw new Error("order_not_found");
  const base = mapPartnerOrder(orderRaw);
  const itemsRaw = Array.isArray(row.items) ? row.items : [];
  const items: PartnerOrderItem[] = itemsRaw
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => ({
      id: str(r.id),
      name: str(r.name || r.product_name, "Item"),
      quantity: num(r.quantity, 1),
      unit_price: num(r.unit_price),
      line_total: num(r.line_total, num(r.unit_price) * num(r.quantity, 1)),
      notes: r.notes != null ? str(r.notes) : r.special_instructions != null
        ? str(r.special_instructions)
        : null,
    }));

  return {
    ...base,
    items,
    notes: orderRaw.notes != null ? str(orderRaw.notes) : null,
  };
}

export async function acceptPartnerOrder(input: {
  partnerId: string;
  tenantId: string;
  orderId: string;
}): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_accept_order", {
    p_partner_id: input.partnerId,
    p_tenant_id: input.tenantId,
    p_order_id: input.orderId,
  });
  if (error) throw error;
  return asRecord(data) ?? {};
}

/** Phase 2: decline a routed order BEFORE accepting it. */
export async function declinePartnerOrder(input: {
  partnerId: string;
  tenantId: string;
  orderId: string;
  reason?: string;
}): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_decline_order", {
    p_partner_id: input.partnerId,
    p_tenant_id: input.tenantId,
    p_order_id: input.orderId,
    p_reason: input.reason ?? null,
  });
  if (error) throw error;
  return asRecord(data) ?? {};
}

export async function assignPartnerDriver(input: {
  partnerId: string;
  tenantId: string;
  orderId: string;
  driverId: string;
  etaMinutes?: number | null;
}): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_assign_driver", {
    p_partner_id: input.partnerId,
    p_tenant_id: input.tenantId,
    p_order_id: input.orderId,
    p_driver_id: input.driverId,
    p_eta_minutes: input.etaMinutes ?? null,
  });
  if (error) throw error;
  return asRecord(data) ?? {};
}

export async function unassignPartnerDriver(input: {
  partnerId: string;
  tenantId: string;
  orderId: string;
}): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_unassign_driver", {
    p_partner_id: input.partnerId,
    p_tenant_id: input.tenantId,
    p_order_id: input.orderId,
  });
  if (error) throw error;
  return asRecord(data) ?? {};
}

/** Blast all eligible partner drivers (or a selected subset). */
export async function blastPartnerDrivers(input: {
  partnerId: string;
  tenantId: string;
  orderId: string;
  driverIds?: string[];
  prepMinutes?: number | null;
}): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  if (input.driverIds && input.driverIds.length > 0) {
    const { data, error } = await supabase.rpc("partner_blast_selected_drivers", {
      p_partner_id: input.partnerId,
      p_tenant_id: input.tenantId,
      p_order_id: input.orderId,
      p_driver_ids: input.driverIds,
      p_prep_minutes: input.prepMinutes ?? null,
    });
    if (error) throw error;
    return asRecord(data) ?? {};
  }
  const { data, error } = await supabase.rpc("partner_blast_drivers", {
    p_partner_id: input.partnerId,
    p_tenant_id: input.tenantId,
    p_order_id: input.orderId,
    p_prep_minutes: input.prepMinutes ?? null,
  });
  if (error) throw error;
  return asRecord(data) ?? {};
}

/** OELOX fallback — no driver picker; backend auto-selects. */
export async function requestOeloxDriver(input: {
  partnerId: string;
  tenantId: string;
  orderId: string;
  etaMinutes?: number | null;
}): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_request_oelox_driver", {
    p_partner_id: input.partnerId,
    p_tenant_id: input.tenantId,
    p_order_id: input.orderId,
    p_eta_minutes: input.etaMinutes ?? null,
  });
  if (error) throw error;
  return asRecord(data) ?? {};
}

export interface PartnerOeloxDriver {
  driver_id: string;
  name: string;
  phone: string | null;
  vehicle_type: string | null;
  vehicle_number: string | null;
  is_online: boolean;
  driver_status: string | null;
  collects_cod: boolean;
}

/** FINAL MODEL — the OELOX global pool partners may optionally assign from. */
export async function listPartnerOeloxDrivers(
  partnerId: string
): Promise<PartnerOeloxDriver[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_list_oelox_drivers", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.drivers) ? row.drivers : [];
  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => ({
      driver_id: str(r.driver_id),
      name: str(r.name, "Driver"),
      phone: r.phone != null ? str(r.phone) : null,
      vehicle_type: r.vehicle_type != null ? str(r.vehicle_type) : null,
      vehicle_number: r.vehicle_number != null ? str(r.vehicle_number) : null,
      is_online: r.is_online === true,
      driver_status: r.driver_status != null ? str(r.driver_status) : null,
      collects_cod: r.collects_cod === true,
    }));
}

/** Assign a specific OELOX global driver (partner must have accepted first). */
export async function assignOeloxDriver(input: {
  partnerId: string;
  tenantId: string;
  orderId: string;
  driverId: string;
  etaMinutes?: number | null;
}): Promise<Record<string, unknown>> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_assign_oelox_driver", {
    p_partner_id: input.partnerId,
    p_tenant_id: input.tenantId,
    p_order_id: input.orderId,
    p_driver_id: input.driverId,
    p_eta_minutes: input.etaMinutes ?? null,
  });
  if (error) throw error;
  return asRecord(data) ?? {};
}

export async function getPartnerFallbackEnabled(
  partnerId: string
): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_get_profile", {
    p_partner_id: partnerId,
  });
  if (error) return false;
  const row = asRecord(data);
  return Boolean(row?.oelox_fallback_enabled);
}

/** Map RPC failures to i18n keys — call `t(mapOrdersRpcError(err))` at the UI. */
export function mapOrdersRpcError(err: unknown): TranslationKey {
  if (!err || typeof err !== "object") return "errors.requestFailed";
  const e = err as { message?: string; code?: string };
  const msg = (e.message || "").toLowerCase();
  if (msg.includes("oelox_fallback_disabled")) return "errors.fallbackDisabled";
  if (msg.includes("partner_drivers_available_use_fleet")) {
    return "errors.driversAvailable";
  }
  if (msg.includes("no_oelox_driver") || msg.includes("no_available_drivers")) {
    return "errors.noOeloxDrivers";
  }
  if (msg.includes("partner_order_not_accepted")) return "errors.acceptFirst";
  if (msg.includes("order_already_accepted")) return "errors.alreadyAccepted";
  if (msg.includes("order_terminal")) return "errors.orderClosed";
  if (msg.includes("partner_not_dispatch_authority")) {
    return "errors.noDispatchAuthority";
  }
  if (msg.includes("already_assigned")) return "errors.alreadyAssigned";
  if (msg.includes("driver_capacity_reached")) return "errors.driverFull";
  if (msg.includes("group_action_required")) return "errors.groupRound";
  if (msg.includes("partner_driver_isolation")) return "errors.driverNotInFleet";
  if (msg.includes("wallet_below_negative_limit")) return "errors.walletBelowLimit";
  return "errors.requestFailed";
}

/** @deprecated Prefer `mapOrdersRpcError` + `t(...)`. */
export function rpcErrorMessage(err: unknown): string {
  const key = mapOrdersRpcError(err);
  const fallbacks: Record<string, string> = {
    "errors.requestFailed": "Request failed.",
    "errors.fallbackDisabled": "OELOX fallback is disabled for this partner.",
    "errors.driversAvailable":
      "Partner drivers are available — assign from your fleet.",
    "errors.noOeloxDrivers": "No OELOX drivers available right now.",
    "errors.acceptFirst": "Accept the order before assigning a driver.",
    "errors.noDispatchAuthority": "No dispatch authority for this business.",
    "errors.alreadyAssigned": "Order is already assigned to another driver.",
    "errors.driverFull":
      "Driver reached the limit — max 2 orders at once (same-store orders are unlimited).",
    "errors.groupRound":
      "This order is part of one round — accept / reject / dispatch the whole round from the group card (mobile app).",
    "errors.driverNotInFleet": "That driver is not in your partner fleet.",
    "errors.walletBelowLimit":
      "Wallet is below its credit limit — dispatch is paused. Ask OELOX to top up.",
  };
  return fallbacks[key] ?? "Request failed.";
}

export function partnerStageTranslationKey(
  stage: PartnerOrderStage | "all"
): TranslationKey {
  return `orders.stages.${stage}` as TranslationKey;
}

/** True when partner has no present/active drivers for fallback gating. */
export function hasAssignablePartnerDrivers(drivers: PartnerDriverRow[]): boolean {
  return drivers.some((d) => d.is_active && d.fleet_type === "partner");
}

export function hasPresentPartnerDrivers(drivers: PartnerDriverRow[]): boolean {
  return drivers.some(
    (d) => d.is_active && d.fleet_type === "partner" && d.is_online
  );
}
